<div class="quotas-container p-6 bg-gray-50 min-h-screen">
  <!-- Page Header -->
  <div class="flex flex-col md:flex-row md:items-center md:justify-between mb-8 pb-4 border-b border-gray-200">
    <div>
      <h1 class="text-2xl font-bold text-gray-900">Agent Leave Quotas</h1>
      <p class="text-gray-500 text-sm mt-1">Configure leave allocations, carry-forward limits, and penalty rates per agent</p>
    </div>
  </div>

  <!-- Filters Card -->
  <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
    <h2 class="text-lg font-semibold text-gray-800 mb-4 flex items-center">
      <i class="fas fa-filter text-green-600 mr-2"></i> Filter Agents
    </h2>
    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div>
        <label class="block text-sm font-medium text-gray-700 mb-1">Filter by Zone</label>
        <select [(ngModel)]="selectedZoneFilter" (change)="onFilterChange()" class="w-full border border-gray-300 rounded-lg p-2.5 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500">
          <option value="">All Zones</option>
          <option *ngFor="let zone of zones" [value]="zone.id">{{ zone.name }}</option>
        </select>
      </div>
      <div>
        <label class="block text-sm font-medium text-gray-700 mb-1">Filter by Agent</label>
        <select [(ngModel)]="selectedAgentId" (change)="onFilterChange()" class="w-full border border-gray-300 rounded-lg p-2.5 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500">
          <option value="">All Agents</option>
          <option *ngFor="let agent of dropdownAgents" [value]="agent.agentId">{{ agent.name }} ({{ agent.agentId }})</option>
        </select>
      </div>
    </div>
  </div>

  <!-- Quotas Grid / Table -->
  <div class="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
    <div class="p-6 border-b border-gray-150 flex items-center justify-between bg-gray-50">
      <h3 class="font-semibold text-gray-800">Agent Quota Settings</h3>
      <span class="text-xs font-semibold px-2.5 py-1 bg-green-100 text-green-800 rounded-full">{{ totalCount }} active agents</span>
    </div>

    <div class="text-center p-12" *ngIf="isLoading && agents.length === 0">
      <i class="fas fa-spinner fa-spin text-3xl text-green-600"></i>
      <p class="text-xs text-gray-400 mt-2">Loading quota settings...</p>
    </div>

    <div class="overflow-x-auto" *ngIf="agents.length > 0">
      <table class="w-full text-left border-collapse">
        <thead>
          <tr class="bg-gray-100 border-b border-gray-200 text-gray-600 text-xs font-semibold uppercase">
            <th class="p-4">Agent</th>
            <th class="p-4">Zones</th>
            <th class="p-4">Available Balance</th>
            <th class="p-4">Allowed Per Month</th>
            <th class="p-4">Informed Penalty</th>
            <th class="p-4">Uninformed Penalty</th>
            <th class="p-4 text-right">Actions</th>
          </tr>
        </thead>
        <tbody class="divide-y divide-gray-200 text-sm text-gray-700">
          <tr *ngFor="let agent of agents" class="hover:bg-gray-50 transition-colors">
            <td class="p-4 font-medium">
              <div class="text-gray-900 font-semibold">{{ agent.name }}</div>
              <div class="text-gray-500 text-xs font-mono">{{ agent.agentId }}</div>
              <div class="text-gray-400 text-xs">{{ agent.phoneNumber }}</div>
            </td>
            <td class="p-4">
              <div class="text-gray-800 text-xs max-w-[180px] truncate" [title]="getZoneNames(agent.zones)">
                {{ getZoneNames(agent.zones) }}
              </div>
            </td>
            <td class="p-4">
              <span *ngIf="quotas[agent.agentId]" class="font-bold px-2 py-1 bg-green-50 border border-green-200 text-green-700 rounded-lg">
                {{ quotas[agent.agentId].availableLeavesBalance }} days
              </span>
              <span *ngIf="!quotas[agent.agentId]" class="text-gray-400 italic text-xs">Loading...</span>
            </td>
            <td class="p-4">
              <span *ngIf="quotas[agent.agentId]">{{ quotas[agent.agentId].allowedLeavesPerMonth }} days</span>
            </td>
            <td class="p-4 font-mono">
              <span *ngIf="quotas[agent.agentId]">₹{{ quotas[agent.agentId].informedLeavePenaltyAmount }}</span>
            </td>
            <td class="p-4 font-mono text-red-600 font-medium">
              <span *ngIf="quotas[agent.agentId]">₹{{ quotas[agent.agentId].uninformedLeavePenaltyAmount }}</span>
            </td>
            <td class="p-4 text-right">
              <button (click)="openConfigModal(agent)" class="px-3 py-1.5 bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200 rounded-lg text-xs font-medium transition-colors inline-flex items-center gap-1">
                <i class="fas fa-edit"></i> Edit Quotas
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Pagination -->
    <app-pagination [page]="currentPage" [totalPages]="totalPages" [pageSize]="pageSize"
      (pageChange)="onPageChange($event)" (pageSizeChange)="onPageSizeChange($event)">
    </app-pagination>
  </div>

  <!-- Dialog: Config Override Modal -->
  <div *ngIf="showConfigModal" class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 backdrop-blur-sm animate-fade-in">
    <div class="bg-white rounded-xl shadow-xl border border-gray-200 max-w-lg w-full p-6 mx-4">
      <h3 class="text-lg font-bold text-gray-900 mb-2">Agent Quota Configuration</h3>
      <p class="text-sm text-gray-500 mb-6">
        Configure limits, allowances, and penalty rates for agent: <span class="font-semibold text-gray-800">{{ configAgent?.name }}</span>
      </p>

      <div *ngIf="!currentQuota" class="p-8 text-center text-gray-400 italic">
        Loading agent configuration...
      </div>

      <div *ngIf="currentQuota" class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div>
          <label class="block text-xs font-semibold text-gray-600 uppercase mb-1">Available Leaves Balance</label>
          <input type="number" step="0.5" [(ngModel)]="configDto.availableLeavesBalance" class="w-full border border-gray-300 rounded-lg p-2.5 text-gray-700 focus:ring-2 focus:ring-blue-500 focus:outline-none" />
        </div>
        <div>
          <label class="block text-xs font-semibold text-gray-600 uppercase mb-1">Allowed Leaves Per Month</label>
          <input type="number" step="0.5" [(ngModel)]="configDto.allowedLeavesPerMonth" class="w-full border border-gray-300 rounded-lg p-2.5 text-gray-700 focus:ring-2 focus:ring-blue-500 focus:outline-none" />
        </div>
        <div>
          <label class="block text-xs font-semibold text-gray-600 uppercase mb-1">Informed Penalty Amount (₹)</label>
          <input type="number" [(ngModel)]="configDto.informedLeavePenaltyAmount" class="w-full border border-gray-300 rounded-lg p-2.5 text-gray-700 focus:ring-2 focus:ring-blue-500 focus:outline-none" />
        </div>
        <div>
          <label class="block text-xs font-semibold text-gray-600 uppercase mb-1">Uninformed Penalty Amount (₹)</label>
          <input type="number" [(ngModel)]="configDto.uninformedLeavePenaltyAmount" class="w-full border border-gray-300 rounded-lg p-2.5 text-gray-700 focus:ring-2 focus:ring-blue-500 focus:outline-none" />
        </div>
      </div>

      <div class="flex justify-end gap-3">
        <button (click)="closeConfigModal()" class="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors">
          Cancel
        </button>
        <button (click)="submitConfigOverride()" [disabled]="savingConfig" class="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50">
          {{ savingConfig ? 'Saving...' : 'Save Configuration' }}
        </button>
      </div>
    </div>
  </div>

  <!-- Notification Toast -->
  <div *ngIf="notification.show" [ngClass]="{
    'bg-green-600': notification.type === 'success',
    'bg-red-600': notification.type === 'error',
    'bg-blue-600': notification.type === 'info'
  }" class="fixed bottom-5 right-5 z-50 text-white px-6 py-3.5 rounded-lg shadow-xl flex items-center transition-all duration-300">
    <i [ngClass]="{
      'fa-check-circle': notification.type === 'success',
      'fa-exclamation-circle': notification.type === 'error',
      'fa-info-circle': notification.type === 'info'
    }" class="fas mr-2"></i>
    <span>{{ notification.message }}</span>
  </div>
</div>
